import { convert, inferUnit } from 'util/unitConversion';

import type { Scenario } from 'store/types/types';

/**
 * Converts the zones in a scenario to a user's preferred units.
 * @param {Object} zones The zones in the scenario
 * @param {number} cropNominalWeight The nominal weight of the crop in the scenario
 * @returns Object
 */
export const convertZonesForLocale = (zones, cropNominalWeight) =>
  Object.keys(zones).reduce((_zones, zoneId) => ({
    ..._zones,
    [zoneId]: {
      ...zones[zoneId],
      meta: {
        ...zones[zoneId].meta,
        area: convert({ from: zones[zoneId].meta.area, to: { u: inferUnit('area').unitKey }}),
        applications: {
          ...zones[zoneId].meta.applications,
          seed: zones[zoneId].meta.applications.seed.map(application => ({
            ...application,
            'estimated-profit': convert({
              from: application['estimated-profit'],
              to: { u: inferUnit('area-cost').unitKey }
            }),
            'estimated-yield': convert({
              from: application['estimated-yield'],
              to: { u: inferUnit('area-density.yield').unitKey },
              cropNominalWeight
            }),
            rate: convert({
              from: application.rate,
              to: { u: inferUnit('seed-density').unitKey }
            }),
            'target-yield': convert({
              from: application['target-yield'],
              to: { u: inferUnit('area-density.yield').unitKey },
              cropNominalWeight
            })
          }))
        }
      }
    }
  }), {});

/**
 * Converts values in a scenario to the user's preferred units
 */
export const convertScenarioForLocale = ({ 
  scenario,
  cropNominalWeight
}: {
  scenario: Scenario; // The full scenario object
  cropNominalWeight: number; //The nominal weight of the crop in the scenario
}) =>
  scenario && {
    ...scenario,
    area: convert({
      from: scenario.area,
      to: { u: inferUnit('area').unitKey }
    }),
    avgEstGrossRevenue: convert({
      from: scenario.avgEstGrossRevenue,
      to: { u: inferUnit('area-cost').unitKey }
    }),
    avgEstYield: convert({
      from: scenario.avgEstYield,
      to: { u: inferUnit('area-density.yield').unitKey },
      cropNominalWeight
    }),
    avgRate: convert({
      from: scenario.avgRate,
      to: { u: inferUnit('seed-density').unitKey }
    }),
    avgTargetYield: convert({
      from: scenario.avgTargetYield,
      to: { u: inferUnit('area-density.yield').unitKey },
      cropNominalWeight
    }),
    costSeedPerAcre: convert({
      from: scenario.costSeedPerAcre,
      to: { u: inferUnit('area-cost').unitKey }
    }),
    zones: convertZonesForLocale(scenario.zones, cropNominalWeight)
  };
